import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { ProfessionKey } from '../professions'
import { buildProviderLocation } from '../locationDisplay'

type SearchResult = {
  userId?: string
  name: string
  profession: string
  displayProfession?: string | null
  stack?: ProfessionKey | string | null
  specialty?: string | null
  title?: string | null
  specialties?: string[]
  profile?: string | null
  location?: string
  state?: string | null
  countryCode?: string | null
  country?: string | null
  rating?: number | null
  ratingCount?: number | null
  image?: string | null
  slug: string
}

const SEARCH_PUBLIC_API = (
  import.meta.env.VITE_SEARCH_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  'https://54uqjwvuke.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')

const STACK_LABELS: Record<ProfessionKey, string> = {
  doctor: 'Medical Doctors',
  allied: 'Allied Health',
  education: 'Education & Tutoring',
  financial: 'Financial',
  migration: 'Migration & Visa',
  legal: 'Legal, Governance & Compliance',
  property: 'Property, Design & Building',
  tech: 'Tech, Creative & Media',
}
const LICENSE_VERIFIED_TOOLTIP =
  'License Verified: This professional’s license has been verified by EUCA Match.'

const AI_SEARCH_EXAMPLES = [
  'I need a GP for a routine health check.',
  'I am looking for a physiotherapist to improve posture and mobility.',
  'I want a piano teacher for weekly beginner lessons.',
  'I need an accountant to help with BAS and quarterly tax.',
  'I need a lawyer to review a service agreement.',
  'I want help preparing my skilled visa application documents.',
  'I am looking for a buyers agent to help me purchase my first home.',
  'I need a web developer to build a website for my small business.',
]

const AI_SEARCH_SESSION_KEY = 'euca.ai-search.session-id'

const getAiSearchSessionId = () => {
  if (typeof window === 'undefined') return 'server'
  try {
    const current = window.localStorage.getItem(AI_SEARCH_SESSION_KEY)
    if (current) return current
    const next = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    window.localStorage.setItem(AI_SEARCH_SESSION_KEY, next)
    return next
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

const fetchJsonWithRetry = async (
  url: string,
  init?: RequestInit,
  attempts = 3,
  delayMs = 250
): Promise<any> => {
  let lastErr: any = null
  for (let i = 0; i < attempts; i += 1) {
    try {
      const res = await fetch(url, { cache: 'no-store', ...(init || {}) })
      const json = await res.json().catch(() => ({}))
      if (res.ok) return json
      throw new Error(json?.error || `Request failed (${res.status})`)
    } catch (err: any) {
      lastErr = err
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * (i + 1)))
      }
    }
  }
  throw lastErr || new Error('Request failed')
}

const mapSearchItem = (item: any): SearchResult => {
  const toSlug = (val: string) =>
    (val || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
  const fallbackSlug = toSlug(item.name || item.userId || 'profile') || 'profile'
  const locationData = buildProviderLocation(item)
  const displayProfession = item.displayProfession || null
  const rating =
    typeof item.rating === 'number'
      ? Number(item.rating)
      : typeof item.rating === 'string'
        ? Number(item.rating)
        : typeof item.ratingAverage === 'number'
          ? Number(item.ratingAverage)
          : typeof item.ratingAverage === 'string'
            ? Number(item.ratingAverage)
            : null
  const ratingCount =
    typeof item.ratingCount === 'number'
      ? item.ratingCount
      : typeof item.ratingCount === 'string'
        ? Number(item.ratingCount)
        : typeof item.reviewCount === 'number'
          ? item.reviewCount
          : null
  const image =
    item.image ||
    item.photoPublicUrl ||
    item.photoUrl ||
    item.photo ||
    null
  return {
    userId: item.userId,
    name: item.name || item.profession || 'Professional',
    profession: displayProfession || item.profession || 'Professional',
    displayProfession,
    stack: item.stack || null,
    specialty: item.specialty || null,
    title: item.title || null,
    specialties: Array.isArray(item.specialties) ? item.specialties : [],
    profile: item.profile || null,
    location: locationData.display || undefined,
    state: locationData.state || null,
    countryCode: locationData.countryCode,
    country: locationData.country,
    rating: Number.isFinite(rating) ? rating : null,
    ratingCount,
    image,
    slug: item.slug || fallbackSlug,
  }
}

const guessStack = (item: SearchResult): ProfessionKey => {
  if (item.stack && STACK_LABELS[item.stack as ProfessionKey]) return item.stack as ProfessionKey
  const name = (item.profession || '').toLowerCase()
  if (name.includes('doctor')) return 'doctor'
  if (name.includes('allied')) return 'allied'
  if (name.includes('visa') || name.includes('migration')) return 'migration'
  if (name.includes('legal') || name.includes('law')) return 'legal'
  if (name.includes('property') || name.includes('real')) return 'property'
  if (name.includes('tech') || name.includes('creative') || name.includes('it')) return 'tech'
  if (name.includes('finance') || name.includes('account')) return 'financial'
  if (name.includes('educat') || name.includes('tutor')) return 'education'
  return 'doctor'
}

const resolveShowcaseStack = (item?: SearchResult | null): ProfessionKey | '' => {
  if (!item) return ''
  const explicit = String(item.stack || '').toLowerCase() as ProfessionKey
  if (explicit && STACK_LABELS[explicit]) return explicit
  return guessStack(item)
}

const normalizeBadgeToken = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const hasLicenseVerifiedBadge = (item?: SearchResult | null) => {
  if (!item) return false
  const stack = resolveShowcaseStack(item)
  if (stack === 'doctor' || stack === 'allied' || stack === 'financial' || stack === 'migration') return true
  const tokens = [
    item.profession,
    item.displayProfession,
    item.specialty,
    item.title,
    ...(Array.isArray(item.specialties) ? item.specialties : []),
  ]
    .filter(Boolean)
    .map((value) => normalizeBadgeToken(String(value)))
    .filter(Boolean)
  if (stack === 'legal') {
    if (tokens.some((token) => token.includes('principal lawyer'))) return true
    if (tokens.some((token) => token.includes('nationally accredited mediator'))) return true
    if (tokens.some((token) => token.includes('naati') && token.includes('translator'))) return true
    return false
  }
  if (stack === 'property') {
    if (tokens.some((token) => token.includes('forensic engineer'))) return true
    if (tokens.some((token) => token.includes('real estate agent'))) return true
    if (tokens.some((token) => /\barchitect\b/.test(token))) return true
    return false
  }
  if (stack === 'education') {
    const labelTokens = [
      item.profession,
      item.displayProfession,
      item.specialty,
      item.title,
    ]
      .filter(Boolean)
      .map((value) => normalizeBadgeToken(String(value)))
      .filter(Boolean)
    if (labelTokens.some((token) => token.includes('education advisor'))) return true
    if (labelTokens.some((token) => /\blecturer\b/.test(token))) return true
    if (labelTokens.some((token) => /\bteacher\b/.test(token))) return true
    return false
  }
  return false
}

const LicenseVerifiedBadge = ({ item }: { item?: SearchResult | null }) => {
  if (!hasLicenseVerifiedBadge(item)) return null
  return (
    <span
      className="license-verified-badge"
      aria-label="License Verified"
      role="img"
      tabIndex={0}
      data-tooltip={LICENSE_VERIFIED_TOOLTIP}
    >
      <ShieldCheck size={13} aria-hidden />
    </span>
  )
}

const inferPrimaryCategoryFromResults = (items: SearchResult[]) => {
  const top = Array.isArray(items) ? items.slice(0, 12) : []
  if (!top.length) {
    return {
      primary: '',
      confidence: 0,
      secondary: null as string | null,
      secondaryConfidence: null as number | null,
    }
  }
  const scored = new Map<string, number>()
  top.forEach((item, idx) => {
    const stack = resolveShowcaseStack(item)
    if (!stack) return
    const weight = 1 / (idx + 1)
    scored.set(stack, (scored.get(stack) || 0) + weight)
  })
  const ranked = Array.from(scored.entries()).sort((a, b) => b[1] - a[1])
  if (!ranked.length) {
    return {
      primary: '',
      confidence: 0,
      secondary: null as string | null,
      secondaryConfidence: null as number | null,
    }
  }
  const total = ranked.reduce((sum, [, score]) => sum + score, 0)
  const [primary, primaryScore] = ranked[0]
  const second = ranked[1] || null
  const includeSecondary = second && second[1] >= primaryScore * 0.55
  return {
    primary,
    confidence: Math.max(0.5, Math.min(0.95, total ? primaryScore / total : 0.5)),
    secondary: includeSecondary ? second[0] : null,
    secondaryConfidence: includeSecondary
      ? Math.max(0.25, Math.min(0.9, total ? second[1] / total : 0.25))
      : null,
  }
}

const fetchAiSearch = async (query: string, sessionId: string) => {
  if (!SEARCH_PUBLIC_API) throw new Error('Search API is unavailable')
  return fetchJsonWithRetry(
    `${SEARCH_PUBLIC_API}/public/ai-search`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, sessionId }),
    },
    2,
    250
  )
}

const fetchSearchApi = async (q: string, profession: string): Promise<SearchResult[]> => {
  if (!SEARCH_PUBLIC_API) return []
  const url = new URL(`${SEARCH_PUBLIC_API}/public/search`)
  if (q.trim()) url.searchParams.set('q', q.trim())
  if (profession) url.searchParams.set('profession', profession)
  const json = await fetchJsonWithRetry(url.toString(), undefined, 2, 250)
  const items = Array.isArray(json?.items) ? json.items : []
  return items.map(mapSearchItem)
}

const logAiSearchClick = async (logId: string, providerId?: string | null) => {
  if (!SEARCH_PUBLIC_API || !logId || !providerId) return
  try {
    await fetch(`${SEARCH_PUBLIC_API}/public/ai-search/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logId, providerId }),
    })
  } catch {
    // best effort only
  }
}

const sanitizeStack = (value: unknown) => {
  const normalized = String(value || '').toLowerCase().trim()
  return STACK_LABELS[normalized as ProfessionKey] ? normalized : ''
}

const cleanProfilePreview = (value?: string | null) => {
  const plain = String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!plain) return ''
  const firstSentenceMatch = plain.match(/^[^.!?]+[.!?]?/)
  const firstSentence = (firstSentenceMatch?.[0] || plain).trim()
  if (firstSentence.length <= 170) return firstSentence
  return `${firstSentence.slice(0, 167).trim()}…`
}

export default function AiSearchPage() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<SearchResult[]>([])
  const [logId, setLogId] = useState('')
  const [aiResult, setAiResult] = useState<any | null>(null)
  const sessionIdRef = useRef(getAiSearchSessionId())

  const dedupeProviders = (items: SearchResult[]) => {
    const seen = new Set<string>()
    return items.filter((item) => {
      const key = `${item.userId || item.slug || item.name}:${resolveShowcaseStack(item) || item.profession || ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  const resolveLabel = (value: string) => {
    const normalized = sanitizeStack(value)
    if (!normalized) return 'Verified professionals'
    return STACK_LABELS[normalized as ProfessionKey]
  }

  const runSearch = useCallback(async (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) {
      setError('Tell us what you need help with to continue.')
      return
    }
    setQuery(trimmed)
    setHasSearched(true)
    setLoading(true)
    setError(null)
    try {
      const ai = await fetchAiSearch(trimmed, sessionIdRef.current)
      setLogId(String(ai?.logId || ''))
      const aiProvided = Array.isArray(ai?.providers)
        ? ai.providers.map((row: any) => mapSearchItem(row))
        : []
      const keywordQuery =
        Array.isArray(ai?.keywords) && ai.keywords.length
          ? ai.keywords.map((k: any) => String(k || '').trim()).filter(Boolean).join(' ')
          : trimmed
      const queryCandidates = Array.from(new Set([trimmed, keywordQuery].filter(Boolean)))
      const parsedIntents = Array.isArray(ai?.intents)
        ? ai.intents
            .map((row: any, idx: number) => {
              const category = sanitizeStack(row?.category || row?.value || row?.stack || '')
              const rawScore = Number(row?.score ?? row?.weight ?? row?.confidence ?? (0.72 - (idx * 0.12)))
              const score = Number.isFinite(rawScore) ? Math.max(0, Math.min(1, rawScore)) : 0
              return { category, score }
            })
            .filter((row: any) => Boolean(row.category))
            .sort((a: any, b: any) => b.score - a.score)
        : []
      const intentTopScore = parsedIntents[0]?.score || 0
      const intentCategories: string[] = Array.from(
        new Set(
          parsedIntents
            .filter((row: any, idx: number) => idx === 0 || (row.score >= 0.22 && row.score >= intentTopScore * 0.45))
            .slice(0, 3)
            .map((row: any) => String(row.category || '').trim())
            .filter(Boolean)
        )
      )
      const primary = sanitizeStack(ai?.primary || '')
      const secondary = sanitizeStack(ai?.secondary || '')
      const categoryCandidates: string[] = Array.from(
        new Set([primary, ...intentCategories, secondary].filter(Boolean))
      ).slice(0, 3)

      const fetchCategoryResults = async (category: string) => {
        if (!category) return []
        for (const candidate of queryCandidates) {
          const items = await fetchSearchApi(candidate, category)
          if (items.length) return items
        }
        return await fetchSearchApi('', category)
      }

      let providers = aiProvided.length ? dedupeProviders(aiProvided) : []
      if (!providers.length && categoryCandidates.length) {
        const collected: SearchResult[] = []
        for (const category of categoryCandidates) {
          const items = await fetchCategoryResults(category)
          if (items.length) collected.push(...items)
          const deduped = dedupeProviders(collected)
          if (deduped.length >= 10) {
            providers = deduped
            break
          }
        }
        if (!providers.length) providers = dedupeProviders(collected)
      }

      const inferred = inferPrimaryCategoryFromResults(providers)
      const aiConfidence = Number(ai?.confidence)
      const normalizedAiConfidence = Number.isFinite(aiConfidence) ? aiConfidence : 0
      const inferredPrimary = sanitizeStack(inferred.primary || '')
      const primaryHitsInTop =
        primary ? providers.slice(0, 5).filter((pro) => resolveShowcaseStack(pro) === primary).length : 0
      const shouldOverridePrimary =
        inferredPrimary &&
        primary &&
        inferredPrimary !== primary &&
        (normalizedAiConfidence < 0.72 || primaryHitsInTop === 0)
      const nextPrimary = shouldOverridePrimary ? inferredPrimary : (primary || inferredPrimary || '')
      let nextSecondary =
        secondary && secondary !== nextPrimary
          ? secondary
          : null
      if (!nextSecondary && inferred.secondary && inferred.secondary !== nextPrimary && Number(inferred.secondaryConfidence || 0) >= 0.35) {
        nextSecondary = sanitizeStack(inferred.secondary || '')
      }
      let nextSecondaryConfidence =
        !nextSecondary
          ? null
          : nextSecondary === secondary
            ? ai?.secondaryConfidence ?? null
            : inferred.secondaryConfidence
      if (nextSecondary) {
        const topWindow = providers.slice(0, 10)
        const secondaryHits = topWindow.filter((pro) => resolveShowcaseStack(pro) === nextSecondary).length
        const secondaryShare = topWindow.length ? secondaryHits / topWindow.length : 0
        const secondaryConfidenceValue = Number(nextSecondaryConfidence ?? 0)
        const secondaryLooksStrong =
          secondaryHits >= 2 &&
          secondaryShare >= 0.34 &&
          secondaryConfidenceValue >= 0.55
        if (!secondaryLooksStrong) {
          nextSecondary = null
          nextSecondaryConfidence = null
        }
      }
      const primaryAligned = nextPrimary
        ? providers.filter((pro) => resolveShowcaseStack(pro) === nextPrimary)
        : []
      const secondaryAligned = nextSecondary
        ? providers.filter((pro) => resolveShowcaseStack(pro) === nextSecondary)
        : []
      let finalProviders = providers
      if (primaryAligned.length) {
        finalProviders = primaryAligned
        const allowSecondaryBlend =
          nextSecondary &&
          Number(nextSecondaryConfidence ?? 0) >= 0.72 &&
          primaryAligned.length < 5
        if (allowSecondaryBlend && secondaryAligned.length) {
          finalProviders = dedupeProviders([
            ...primaryAligned,
            ...secondaryAligned,
          ])
        }
      }

      setAiResult({
        ...ai,
        primary: nextPrimary || null,
        secondary: nextSecondary || null,
        secondaryConfidence: nextSecondaryConfidence,
        confidence:
          nextPrimary && nextPrimary !== primary
            ? Math.max(normalizedAiConfidence, Number(inferred.confidence || 0))
            : normalizedAiConfidence,
      })
      setResults(finalProviders)
    } catch (err: any) {
      setError(err?.message || 'AI search is unavailable right now.')
      setResults([])
      setAiResult(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const primaryLabel = aiResult?.primary ? resolveLabel(aiResult.primary) : ''
  const secondaryLabel = aiResult?.secondary ? resolveLabel(aiResult.secondary) : ''
  const showCompactSearch = hasSearched && (!!aiResult || results.length > 0 || !!error)
  const duplicateSlugs = useMemo(() => {
    const counts = new Map<string, number>()
    results.forEach((pro) => {
      if (!pro || !pro.slug) return
      const cleanSlug = String(pro.slug).replace(/-/g, '')
      counts.set(cleanSlug, (counts.get(cleanSlug) || 0) + 1)
    })
    return new Set(Array.from(counts.entries()).filter(([, c]) => c > 1).map(([slug]) => slug))
  }, [results])
  const requestProfession = useMemo(() => {
    const fromAi = sanitizeStack(aiResult?.primary || '')
    if (fromAi) return fromAi as ProfessionKey
    const fromResults = resolveShowcaseStack(results[0] || null)
    return (fromResults || 'doctor') as ProfessionKey
  }, [aiResult?.primary, results])
  const requestSpecialty = useMemo(() => {
    const trimmed = query.trim()
    if (trimmed) return trimmed
    const fromAi = String(aiResult?.primaryLabel || aiResult?.primary || '').trim()
    return fromAi || 'General support'
  }, [query, aiResult?.primary, aiResult?.primaryLabel])
  const openTopMatchesRequest = () => {
    const params = new URLSearchParams()
    if (requestSpecialty) {
      params.set('specialty', requestSpecialty)
      params.set('reason', requestSpecialty)
    }
    params.set('aiRequest', '1')
    navigate(`/results/${requestProfession}?${params.toString()}`)
  }

  return (
    <div className="container clients-ai-search-page">
      {showCompactSearch && (
        <section className="clients-ai-hero clients-ai-hero-compact">
          <p className="eyebrow">Refine your search</p>
          <form
            className="clients-ai-compact-form"
            onSubmit={(e) => {
              e.preventDefault()
              runSearch(query)
            }}
          >
            <input
              type="search"
              className="input clients-ai-compact-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Update your request and search again"
            />
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Searching…' : 'Search again'}
            </button>
          </form>
        </section>
      )}

      {!showCompactSearch && (
        <section className="clients-ai-hero">
          <p className="eyebrow">AI Search</p>
          <h1 className="clients-ai-title">What do you need help with?</h1>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              runSearch(query)
            }}
          >
            <textarea
              className="input clients-ai-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tell us what you’re looking for, in your own words."
            />
            <div className="clients-ai-actions">
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? 'Finding matches…' : 'Find Verified Professionals'}
              </button>
            </div>
          </form>
          <div className="suggestion-grid clients-ai-examples">
            {AI_SEARCH_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                className="suggestion-chip"
                onClick={() => {
                  setQuery(example)
                  runSearch(example)
                }}
              >
                {example}
              </button>
            ))}
          </div>
        </section>
      )}

      {error && !loading && <div className="banner error" role="alert">{error}</div>}

      {aiResult && (
        <section className="clients-ai-summary">
          <article className="clients-ai-summary-card">
            <p className="eyebrow">Primary recommendation</p>
            <h3>{primaryLabel || 'Verified professionals'}</h3>
            <p className="muted">Top matches from verified professionals.</p>
            {aiResult?.primary ? (
              <Link className="btn btn-outline" to={`/clients?profession=${encodeURIComponent(aiResult.primary)}`}>
                View all {primaryLabel}
              </Link>
            ) : null}
          </article>
          {aiResult?.secondary ? (
            <article className="clients-ai-summary-card secondary">
              <p className="eyebrow">You may also need</p>
              <h3>{secondaryLabel}</h3>
              <p className="muted">If relevant, explore this category too.</p>
              <Link className="btn btn-outline" to={`/clients?profession=${encodeURIComponent(aiResult.secondary)}`}>
                View {secondaryLabel}
              </Link>
            </article>
          ) : null}
        </section>
      )}

      <section className="clients-search">
        {!loading && results.length > 0 && (
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>Ready to contact professionals?</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              We will notify the top 3 matching professionals automatically.
            </p>
            <button className="btn btn-primary" onClick={openTopMatchesRequest}>
              Continue request
            </button>
          </div>
        )}
        {!loading && hasSearched && !results.length && !error && (
          <p className="muted">No professionals found yet in this category.</p>
        )}
        {loading && <p className="muted">Matching verified professionals…</p>}
        {!loading && results.length > 0 && (
          <div className="clients-search-results">
            {results.map((item) => {
              const stack = resolveShowcaseStack(item) || 'doctor'
              const professionLabel =
                item.displayProfession ||
                item.profession ||
                STACK_LABELS[stack as ProfessionKey] ||
                'Professional'
              const profileSlug = (item.slug || item.name || 'provider').toLowerCase().replace(/[^a-z0-9]+/g, '')
              const ratingLabel = Number(item.rating) > 0 ? Number(item.rating).toFixed(1) : null
              const starCount =
                Number(item.rating) > 0
                  ? Math.max(1, Math.min(5, Math.round(Number(item.rating) || 0)))
                  : 0
              const stars = starCount > 0 ? '★'.repeat(starCount) : ''
              const specialties = (item.specialties || []).filter(Boolean).slice(0, 4)
              const profilePreview = cleanProfilePreview(item.profile)
              const duplicateKey = duplicateSlugs.has((item.slug || '').replace(/-/g, ''))
              return (
                <Link
                  key={(item.userId || item.slug || profileSlug) + stack}
                  className="clients-search-card clients-ai-result-card"
                  to={`/profile/${profileSlug}${duplicateKey ? `?bio=${encodeURIComponent(stack)}` : ''}`}
                  state={{ provider: item }}
                  onClick={() => {
                    if (logId) void logAiSearchClick(logId, item.userId || item.slug || '')
                  }}
                >
                  <div className="clients-search-photo">
                    <img src={item.image || '/logo.png'} alt={item.name} loading="lazy" />
                    <span className="clients-search-pill">{professionLabel}</span>
                  </div>
                  <div className="clients-search-body">
                    <div className="clients-search-row">
                      <h3 className="provider-name-line">
                        <span>{item.name}</span>
                        <LicenseVerifiedBadge item={item} />
                      </h3>
                      {ratingLabel ? (
                        <div className="clients-search-rating clients-ai-result-rating" aria-label={`${ratingLabel} out of 5`}>
                          <div className="rating-row">
                            <span className="value">{ratingLabel}</span>
                            <span aria-hidden>
                              {stars}
                            </span>
                          </div>
                          <div className="reviews-count">{Number(item.ratingCount || 0).toLocaleString()} reviews</div>
                        </div>
                      ) : null}
                    </div>
                    <p className="clients-ai-result-profession">{professionLabel}</p>
                    <div className="clients-search-meta-row">
                      {specialties.length ? (
                        <p className="clients-ai-result-meta">{specialties.join(' · ')}</p>
                      ) : null}
                      {item.location ? (
                        <p className="clients-ai-result-location">{item.location}</p>
                      ) : null}
                      {!specialties.length && !item.location ? (
                        <p className="clients-ai-result-meta">Verified professional</p>
                      ) : null}
                    </div>
                    {profilePreview ? <p className="clients-ai-result-snippet">{profilePreview}</p> : null}
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
